import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Coins, Settings, ExternalLink, LayoutDashboard, Gift } from "lucide-react";
import { saas, errMsg } from "@/lib/saasApi";
import { useAuth } from "@/saas/AuthContext";
import { PACKAGES } from "@/lib/data";
import { PriceCard } from "@/components/PriceCard";
import { getPlan } from "@/lib/plans";
import { SupportLine } from "@/components/SupportLine";
import { useI18n, DATE_LOCALE, usePageTitle } from "@/i18n";
import { RichText } from "@/components/RichText";

export default function Billing() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  usePageTitle("titles.billing");
  const locale = DATE_LOCALE[lang] || "en-GB";
  const nav = useNavigate();
  const isAdmin = user?.role === "AGENCY_ADMIN";
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState("");   // slug currently opening checkout
  const [portalLoading, setPortalLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [confirmedBusiness, setConfirmedBusiness] = useState(false);
  const [switchTo, setSwitchTo] = useState(null);   // target slug for the confirmation modal
  const [switching, setSwitching] = useState(false); // in-flight guard (blocks double-clicks)
  const [switchResult, setSwitchResult] = useState(null); // {type:'success'|'processing'|'error', text}

  const load = useCallback(() => saas.billing().then(({ data }) => setData(data)).catch((e) => toast.error(errMsg(e))), []);
  useEffect(() => { load(); }, [load]);

  // Open a Stripe TEST MODE Checkout session for the chosen plan. The backend
  // resolves the price id and records the plan as pending only — no credits are
  // granted and no subscription is activated until payment is confirmed later.
  const choose = async (slug) => {
    if (saving || !isAdmin) return;
    if (!acceptedTerms || !confirmedBusiness) {
      toast.error(t("bill.errAccept"));
      return;
    }
    setSaving(slug);
    try {
      const { data: res } = await saas.createCheckoutSession(slug);
      if (res?.url) { window.location.href = res.url; return; } // redirect to hosted Stripe Checkout
      toast.error(t("bill.errCheckout"));
      setSaving("");
    } catch (e) {
      toast.error(e?.response?.status === 409 ? t("bill.errActiveSub") : t("bill.errCheckout"));
      setSaving("");
    }
  };

  // Open the Stripe Customer Portal (manage payment method, invoices, cancel at period end).
  const manageSubscription = async () => {
    if (portalLoading || !isAdmin) return;
    setPortalLoading(true);
    try {
      const { data: res } = await saas.customerPortal();
      if (res?.url) { window.location.href = res.url; return; }
      toast.error(t("bill.errPortal"));
      setPortalLoading(false);
    } catch {
      toast.error(t("bill.errPortal"));
      setPortalLoading(false);
    }
  };

  // Immediate plan switch on the existing subscription. Credits are added only after Stripe
  // confirms the new payment (invoice.paid webhook) — we never fake the result client-side.
  const confirmSwitch = async () => {
    if (switching || !switchTo) return;
    setSwitching(true);
    try {
      const { data: res } = await saas.switchPlan(switchTo);
      const target = res && res.status;
      if (target === "requires_action" && res.hostedInvoiceUrl) {
        // Payment needs completion / authentication (SCA) — send the customer to Stripe.
        window.location.href = res.hostedInvoiceUrl;
        return;
      }
      const info = planInfo(switchTo);
      setSwitchTo(null);
      if (target === "switched") {
        await load(); // read the real resulting balance + next billing date from the backend
        setSwitchResult({ type: "success", plan: info?.name, credits: info?.monthlyCredits });
      } else {
        // processing — payment is being finalised; the webhook will add credits shortly
        await load();
        setSwitchResult({ type: "processing" });
      }
    } catch (e) {
      setSwitchTo(null);
      setSwitchResult({ type: "error", text: e?.response?.status === 409
        ? t("bill.errSwitch409")
        : t("bill.errSwitchFailed") });
    } finally {
      setSwitching(false);
    }
  };

  if (!data) return <p className="text-sm text-re-muted" data-testid="billing-loading">{t("common.loading")}</p>;

  const planName = (slug) => getPlan(slug)?.name || slug;
  const planInfo = (slug) => (data.plans || []).find((p) => p.id === slug) || null;
  const b = data.billing || {};
  const state = b.state || (data.subscriptionStatus === "active" ? "active" : "none");
  const isActive = ["active", "cancel_scheduled", "trialing"].includes(state); // access still granted
  const hasStripeCustomer = !!data.hasStripeCustomer;
  const NOTICES = {
    past_due: { ...t("bill.notices.past_due"), hasPortal: true },
    unpaid: { ...t("bill.notices.unpaid"), hasPortal: true },
    incomplete: { ...t("bill.notices.incomplete"), hasPortal: true },
    incomplete_expired: { ...t("bill.notices.incomplete_expired"), hasPortal: false },
    paused: { ...t("bill.notices.paused"), hasPortal: true },
    cancelled: { ...t("bill.notices.cancelled"), hasPortal: false },
  };
  const attention = NOTICES[state];
  const fmtDate = (iso) => { try { return iso ? new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "short", year: "numeric" }) : "—"; } catch { return "—"; } };
  const currentPlan = isActive
    ? { label: t("bill.currentPlan"), value: planName(data.planId), cls: "text-emerald-700" }
    : data.pendingPlanId
      ? { label: t("bill.pendingPlan"), value: planName(data.pendingPlanId), cls: "text-re-red" }
      : { label: t("bill.currentPlan"), value: t("bill.noPlan"), cls: "text-re-muted" };
  // Merge non-translatable pricing meta (id, price, recommended) with localized copy.
  const packages = PACKAGES.map((p, i) => ({ ...p, ...(t("pricing.packages")[i] || {}), period: t("pricing.perMonth") }));

  return (
    <div className="space-y-8" data-testid="billing-page">
      {/* 1. Heading */}
      <div>
        <h1 className="font-heading text-2xl font-bold text-re-charcoal md:text-3xl">{t("bill.title")}</h1>
        <p className="mt-2 text-sm text-re-muted md:text-base">{t("bill.sub")}</p>
      </div>

      {/* Billing notice — driven by the derived lifecycle state (past_due / unpaid / incomplete /
          incomplete_expired / paused / cancelled). Active + scheduled-cancel are shown in the summary. */}
      {attention && (
        <div className="rounded-2xl border border-amber-300 bg-amber-50 p-6" data-testid="billing-notice">
          <p className="font-semibold text-re-charcoal" data-testid="billing-notice-title">{attention.title}</p>
          <p className="mt-1 text-sm text-re-muted" data-testid="billing-notice-message">{attention.text}</p>
          {attention.hasPortal && isAdmin && hasStripeCustomer && (
            <button onClick={manageSubscription} disabled={portalLoading} data-testid="notice-manage-subscription"
              className="mt-3 rounded-full bg-re-charcoal px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-black disabled:opacity-60">
              {portalLoading ? t("bill.openingPortal") : attention.portal}
            </button>
          )}
        </div>
      )}

      {/* 2 + 3. Available credits & current plan */}
      <div className="grid gap-4 sm:grid-cols-2">
        <div className="flex items-center justify-between rounded-2xl border border-re-line bg-white p-6" data-testid="billing-credits">
          <div>
            <p className="text-sm text-re-muted">{t("bill.creditsLabel")}</p>
            <p className="font-heading text-2xl font-bold text-re-charcoal" data-testid="billing-available-credits">{data.availableCredits} <span className="text-base font-medium text-re-muted">{t("bill.projectCredits")}</span></p>
            {data.complimentaryCredit ? (
              <p className="mt-1 inline-flex items-center gap-1.5 rounded-full bg-re-red/10 px-2.5 py-1 text-xs font-medium text-re-red" data-testid="billing-complimentary-label"><Gift className="h-3.5 w-3.5" /> {t("bill.complimentary")}</p>
            ) : (
              <p className="mt-1 text-xs text-re-muted">{t("bill.creditExplainer")}</p>
            )}
          </div>
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-re-red/10"><Coins className="h-5 w-5 text-re-red" /></span>
        </div>
        <div className="rounded-2xl border border-re-line bg-white p-6" data-testid="billing-current-plan">
          <p className="text-sm text-re-muted">{currentPlan.label}</p>
          <p className={`mt-0.5 font-heading text-2xl font-bold ${currentPlan.cls}`} data-testid="current-plan-value">{currentPlan.value}</p>
          {!isActive && <p className="mt-1 text-xs text-re-muted">{t("bill.creditsAfterPayment")}</p>}
        </div>
      </div>

      {/* Active subscription summary */}
      {isActive && (
        <div className="rounded-2xl border border-emerald-200 bg-emerald-50/50 p-6" data-testid="active-subscription">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-sm text-re-muted">{t("bill.currentPlan")}</p>
              <p className="font-heading text-xl font-bold text-re-charcoal" data-testid="active-plan-name">{b.planName || planName(data.planId)}</p>
            </div>
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold ${state === "cancel_scheduled" ? "bg-amber-100 text-amber-700" : state === "trialing" ? "bg-blue-100 text-blue-700" : "bg-emerald-100 text-emerald-700"}`} data-testid="active-status">{b.label || t("bill.active")}</span>
          </div>
          <div className="mt-4 grid gap-4 sm:grid-cols-3">
            <div><p className="text-xs text-re-muted">{t("bill.monthlyPrice")}</p><p className="font-semibold text-re-charcoal" data-testid="active-monthly-price">€{b.monthlyPrice}<span className="text-xs font-normal text-re-muted"> {t("bill.perMonthVat")}</span></p></div>
            <div><p className="text-xs text-re-muted">{t("bill.monthlyCredits")}</p><p className="font-semibold text-re-charcoal" data-testid="active-monthly-credits">{b.monthlyCredits}</p></div>
            <div><p className="text-xs text-re-muted">{b.dateLabel || t("bill.nextBilling")}</p><p className="font-semibold text-re-charcoal" data-testid="active-next-billing">{fmtDate(b.date || b.currentPeriodEnd)}</p></div>
          </div>

          {/* Billing information (where the customer provided it during checkout) */}
          {(b.companyName || b.taxId || b.address?.country) && (
            <div className="mt-4 rounded-xl border border-emerald-200 bg-white p-4" data-testid="billing-information">
              <p className="text-xs font-semibold uppercase tracking-wide text-re-muted">{t("bill.billingInfo")}</p>
              {b.companyName && <p className="mt-1 text-sm font-medium text-re-charcoal" data-testid="billing-company">{b.companyName}</p>}
              {(b.address?.line1 || b.address?.city || b.address?.country) && (
                <p className="text-sm text-re-muted" data-testid="billing-address">
                  {[b.address?.line1, b.address?.postalCode, b.address?.city, b.address?.country].filter(Boolean).join(", ")}
                </p>
              )}
              {b.taxId && <p className="text-sm text-re-muted" data-testid="billing-taxid">{t("bill.vatTaxId")} {b.taxId}</p>}
            </div>
          )}

          {b.cancelScheduled && (
            <p className="mt-3 text-sm font-medium text-amber-700" data-testid="cancel-scheduled">{t("bill.cancelScheduled", { date: fmtDate(b.date || b.currentPeriodEnd) })}</p>
          )}

          {/* Actions */}
          <div className="mt-5 flex flex-wrap gap-3">
            {isAdmin && (
              <button onClick={manageSubscription} disabled={portalLoading} data-testid="manage-subscription"
                className="inline-flex items-center gap-2 rounded-full bg-re-charcoal px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-black disabled:opacity-60">
                <Settings className="h-4 w-4" /> {portalLoading ? t("bill.openingPortal") : t("bill.manageSubscription")}
              </button>
            )}
            <a href="/" data-testid="view-website"
              className="inline-flex items-center gap-2 rounded-full border border-re-line px-5 py-2.5 text-sm font-semibold text-re-ink transition-colors hover:border-re-charcoal">
              <ExternalLink className="h-4 w-4" /> {t("bill.viewWebsite")}
            </a>
            <button onClick={() => nav("/dashboard")} data-testid="back-to-dashboard"
              className="inline-flex items-center gap-2 rounded-full border border-re-line px-5 py-2.5 text-sm font-semibold text-re-ink transition-colors hover:border-re-charcoal">
              <LayoutDashboard className="h-4 w-4" /> {t("bill.backToDashboard")}
            </button>
          </div>
        </div>
      )}

      {/* 4. Package cards. When a subscription is active these are informational only
          ("Other plans") — an org may hold at most one active subscription. */}
      <div>
        <h2 className="font-heading text-lg font-semibold text-re-charcoal" data-testid="plans-heading">{isActive ? t("bill.otherPlans") : t("bill.chooseYourPlan")}</h2>
        {isActive && (
          <p className="mt-1 text-sm text-re-muted" data-testid="switch-plan-help">
            <RichText text={isAdmin ? t("bill.switchHelpAdmin") : t("bill.switchHelpUser")} />
          </p>
        )}
        {!isAdmin && !isActive && <p className="mt-1 text-sm text-re-muted" data-testid="user-view-only">{t("bill.viewOnly")}</p>}

        {/* Legal acceptance — required before opening checkout (business/professional flow). */}
        {!isActive && isAdmin && (
          <div className="mt-5 space-y-3 rounded-2xl border border-re-line bg-white p-5" data-testid="checkout-acceptance">
            <label className="flex cursor-pointer items-start gap-3 text-sm text-re-charcoal">
              <input type="checkbox" checked={acceptedTerms} onChange={(e) => setAcceptedTerms(e.target.checked)} data-testid="accept-terms-checkbox" className="mt-0.5 h-4 w-4 rounded border-re-line text-re-red focus:ring-re-red" />
              <span><RichText text={t("bill.acceptTerms")} /></span>
            </label>
            <label className="flex cursor-pointer items-start gap-3 text-sm text-re-charcoal">
              <input type="checkbox" checked={confirmedBusiness} onChange={(e) => setConfirmedBusiness(e.target.checked)} data-testid="confirm-business-checkbox" className="mt-0.5 h-4 w-4 rounded border-re-line text-re-red focus:ring-re-red" />
              <span>{t("bill.confirmBusiness")}</span>
            </label>
          </div>
        )}

        <div className="mx-auto grid max-w-6xl items-center gap-6 px-1 pt-6 pb-4 md:grid-cols-3 md:gap-5" data-testid="plan-cards">
          {packages.map((p, i) => {
            const isCurrent = isActive && data.planId === p.id;
            const ctaLabel = saving === p.id ? t("bill.openingCheckout")
              : isCurrent ? t("bill.currentPlan")
              : isActive ? t("bill.switchTo", { name: p.name })
              : t("bill.choose", { name: p.name });
            const disabled = isCurrent || !isAdmin
              || (isActive ? switching : (!!saving || !acceptedTerms || !confirmedBusiness));
            return (
              <PriceCard key={p.id} pkg={p} index={i} vatLabel={t("app.billing.exclVat")}
                ctaLabel={ctaLabel}
                ctaTestId={isActive ? `switch-plan-${p.id}` : `choose-plan-${p.id}`}
                disabled={disabled}
                onSelect={() => (isActive ? setSwitchTo(p.id) : choose(p.id))} />
            );
          })}
        </div>
      </div>

      {/* Plan-switch confirmation modal */}
      {switchTo && (() => {
        const info = planInfo(switchTo);
        const price = info?.intendedPrice;
        const credits = info?.monthlyCredits;
        const targetName = info?.name || planName(switchTo);
        const currentName = b.planName || planName(data.planId);
        return (
          <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" data-testid="switch-confirm-modal">
            <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl">
              <h2 className="font-heading text-lg font-bold text-re-charcoal" data-testid="switch-modal-title">{t("bill.switchTitle", { name: targetName })}</h2>
              <div className="mt-3 space-y-2 text-sm text-re-charcoal">
                <p><RichText text={t("bill.switchBody1", { current: currentName, target: targetName })} /></p>
                <p><RichText text={t("bill.switchBody2", { price })} /></p>
                <p><RichText text={t("bill.switchBody3", { credits })} /></p>
                <p><RichText text={t("bill.switchBody4", { target: targetName, current: currentName })} /></p>
                {b.cancelScheduled && (
                  <p className="rounded-lg bg-amber-50 p-3 text-amber-800" data-testid="switch-resume-note"><RichText text={t("bill.switchResume")} /></p>
                )}
              </div>
              <div className="mt-6 flex flex-col gap-2 sm:flex-row sm:justify-end">
                <button onClick={() => setSwitchTo(null)} disabled={switching} data-testid="switch-cancel"
                  className="rounded-full border border-re-line px-5 py-2.5 text-sm font-semibold text-re-ink transition-colors hover:border-re-charcoal disabled:opacity-60">
                  {t("common.cancel")}
                </button>
                <button onClick={confirmSwitch} disabled={switching} data-testid="switch-confirm"
                  className="rounded-full bg-re-red px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-re-deepred disabled:opacity-60">
                  {switching ? t("bill.processing") : t("bill.confirmPay", { price })}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Plan-switch result modal */}
      {switchResult && (
        <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" role="dialog" aria-modal="true" data-testid="switch-result-modal">
          <div className="w-full max-w-md rounded-2xl bg-white p-6 shadow-2xl">
            {switchResult.type === "success" ? (
              <>
                <h2 className="font-heading text-lg font-bold text-emerald-700" data-testid="switch-success-title">{t("bill.successTitle")}</h2>
                <p className="mt-3 text-sm text-re-charcoal" data-testid="switch-success-text">
                  <RichText text={t("bill.successText", { plan: switchResult.plan, credits: switchResult.credits, total: data.availableCredits, date: fmtDate(b.date || b.currentPeriodEnd) })} />
                </p>
              </>
            ) : switchResult.type === "processing" ? (
              <>
                <h2 className="font-heading text-lg font-bold text-re-charcoal" data-testid="switch-processing-title">{t("bill.processingTitle")}</h2>
                <p className="mt-3 text-sm text-re-charcoal" data-testid="switch-processing-text">{t("bill.processingText")}</p>
              </>
            ) : (
              <>
                <h2 className="font-heading text-lg font-bold text-re-red" data-testid="switch-error-title">{t("bill.errorTitle")}</h2>
                <p className="mt-3 text-sm text-re-charcoal" data-testid="switch-error-text">{switchResult.text}</p>
              </>
            )}
            <div className="mt-6 flex justify-end">
              <button onClick={() => setSwitchResult(null)} data-testid="switch-result-close"
                className="rounded-full bg-re-charcoal px-5 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-black">
                {t("common.close")}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* How credits work */}
      <details className="rounded-2xl border border-re-line bg-white p-5" data-testid="how-credits-work">
        <summary className="cursor-pointer font-semibold text-re-charcoal">{t("bill.howTitle")}</summary>
        <p className="mt-3 text-sm leading-relaxed text-re-muted">
          <RichText text={t("bill.howText")} />
        </p>
      </details>

      {/* 5. Info */}
      {!isActive && <p className="text-center text-xs text-re-muted" data-testid="billing-future-note">{t("bill.futureNote")}</p>}

      <SupportLine className="justify-center pt-2" testid="billing-support" />
    </div>
  );
}
